import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { get, isEmpty } from 'lodash'
import { Dispatch } from 'redux'
import {
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
} from '@labrav/common'

import {
  fetchSellerData,
  getFlyoutNewOnboardingFormData,
  getFlyOutOnboardingFormData,
  patchCheckMicrosoftConnection,
  putFlyoutNewOnboardingFormData,
  putOnboardingFormData,
} from '../../api/marketplace/index'
import { getPageMarkerMapping } from '../../common/utils/getPageMarkerMapping'
import {
  flyOutOnboardingCompletedSuccessfully,
  NotificationSavedSuccessMessageV2,
} from '../../common/utils/messagesContants'
import {
  getFlyoutListingData,
  getFlyoutListingDataMicrosoft,
} from '../flyOutSyncs/action'
import type { AppDispatch, RootState } from '../../store/types'
import { PartnerType, type AccountData } from '../../types/external'
import { CloudType } from '../../types/index'
import {
  FlyoutOnboardingForm,
  FlyoutOnboardingSectionName,
  type CheckMicrosoftConnectionPayload,
  type FlyOutOnboardingChangeType,
  type FlyoutCloudSettingsSectionsProps,
  type MicrosoftOnboarding,
  type SendFlyoutOnboardingProps,
  type TestConnectionStatusProps,
} from './types'

import {
  applyDefaultCloudSettings,
  buildLockMetadataForCurrentUser,
  convertApiResponseToReducer,
  convertMicrosoftFormToApiPayload,
  convertReducerToMicrosoftForm,
  isChangeInOnboardingData,
  mapCloudCurrenciesToCurrencies,
  normalizeOnboardingApiDetails,
  processFlyoutOnboarding,
  processPartnerDetailsData,
  setInitialPartnerData,
  shouldAcquireOnboardingLock,
} from './utils'

export { FlyoutOnboardingForm }
export type { FlyOutOnboardingChangeType }
export { checkAllCompleted } from './utils'
export { processPartnerDetailsData } from './utils'

export const setOnboardingFormData = (
  onboardingFormData: Record<string, unknown>
) => ({
  type: FlyoutOnboardingForm.SET_FLYOUT_ONBOARDING_FORM,
  payload: onboardingFormData,
})

export const updateFlyOutOnboardingData = (
  data: FlyOutOnboardingChangeType
) => ({
  type: FlyoutOnboardingForm.UPDATE_FLYOUT_ONBOARDING_FORM,
  payload: data,
})

export const setFlyOutTestConnectionStatus = (
  status: TestConnectionStatusProps
) => ({
  type: FlyoutOnboardingForm.UPDATE_FLYOUT_TEST_CONNECTION_STATUS,
  payload: status,
})

export const updateMicrosoftOnboardingState = (
  microsoftOnboardingForm: MicrosoftOnboarding
) => ({
  type: FlyoutOnboardingForm.UPDATE_MICROSOFT_ONBOARDING_FORM,
  payload: microsoftOnboardingForm,
})

export const getFlyOutOnboardingData =
  (
    partnerId: string,
    tenantPartnerId: string,
    cloudType: CloudType,
    isOnboarded?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.FLYOUT_ONBOARDING_DATA))
    try {
      const { data } = await getFlyOutOnboardingFormData(partnerId)
      const camelizedData = get(camelize(data), 'onboardingData') || {}
      if (isEmpty(camelizedData)) {
        const partnerData = getState().AccountData[PartnerType.User]
          .partnerData as AccountData
        const initialFlyoutOnboardingFormData = applyDefaultCloudSettings(
          setInitialPartnerData(partnerData),
          partnerId
        )
        await dispatch(
          setOnboardingFormData({
            ...camelize(initialFlyoutOnboardingFormData),
          })
        )
      } else {
        const updatedOnboardingData = JSON.parse(
          JSON.stringify(camelizedData).replaceAll('\\\\"', '"')
        )

        const details = updatedOnboardingData.details || {}
        normalizeOnboardingApiDetails(details)

        const updatedOnboardingJson = {
          ...details,
          lockMetadata: {
            ...updatedOnboardingData.lockMetadata,
          },
        }

        const user = getState().userProfile
        const pageMarkerMapping: Record<string, string> = getPageMarkerMapping()
        if (!isOnboarded) {
          if (
            shouldAcquireOnboardingLock(
              updatedOnboardingJson?.lockMetadata?.lastActivityAt
            )
          ) {
            updatedOnboardingJson.lockMetadata =
              buildLockMetadataForCurrentUser(
                updatedOnboardingJson.lockMetadata,
                user
              )
            await dispatch(
              sendFlyOutOnboardingData({
                partnerId,
                tenantPartnerId,
                isAutoSave: true,
                onboardingForm: updatedOnboardingJson,
                pageMarkerMapping,
                page: FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS,
                status: 'lockAcquire',
              })
            )
          } else {
            await dispatch(setOnboardingFormData({ ...updatedOnboardingJson }))
          }
        } else {
          await dispatch(setOnboardingFormData({ ...updatedOnboardingJson }))
        }
      }
    } catch (error) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.FLYOUT_ONBOARDING_DATA))
    }
  }

export const getFlyoutMicrosoftOnboardingFormData =
  (accountId: string, cloudType: CloudType, shouldSendData?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_FLYOUT_ONBOARDING_DATA))
    try {
      const { data } = await getFlyoutNewOnboardingFormData(
        accountId,
        cloudType
      )
      const microsoftData = convertApiResponseToReducer(data)

      if (microsoftData.lockMetadata) {
        const user = getState().userProfile

        if (
          shouldAcquireOnboardingLock(microsoftData.lockMetadata.lastActivityAt)
        ) {
          microsoftData.lockMetadata = buildLockMetadataForCurrentUser(
            microsoftData.lockMetadata,
            user
          )
          if (shouldSendData) {
            await dispatch(
              sendMarketplaceOnboardingData(
                accountId,
                convertReducerToMicrosoftForm(microsoftData),
                cloudType
              )
            )
          }
        }
      }

      await dispatch(updateMicrosoftOnboardingState(microsoftData))
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            error as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: false,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_FLYOUT_ONBOARDING_DATA))
    }
  }

export const sendFlyOutOnboardingData =
  ({
    partnerId,
    tenantPartnerId,
    isAutoSave,
    onboardingForm,
    pageMarkerMapping,
    page,
    status,
    isOnboarded,
    callBackFlyoutOnboarding,
  }: SendFlyoutOnboardingProps) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      !isAutoSave &&
        (await dispatch(startLoading(LoadingTypes.SUBMIT_FLYOUT_ONBOARDING)))
      const getOnboardingStateData =
        getState()?.flyOutOnboarding?.onboardingForm
      const isFormUpdated = isChangeInOnboardingData(
        onboardingForm,
        getOnboardingStateData
      )
      const onboardingProcessedData = processFlyoutOnboarding({
        isAutoSave,
        onboardingForm,
        page,
        pageMarkerMapping,
        status,
        isFormUpdated,
        partnerId,
      })
      const onboardingPayload = JSON.parse(
        JSON.stringify(onboardingProcessedData).replaceAll('\\"', '\\\\\\"')
      )

      if (
        page === FlyoutOnboardingSectionName.LABRA_PARTNER_DETAILS &&
        !isAutoSave
      ) {
        await processPartnerDetailsData(
          tenantPartnerId,
          onboardingForm,
          dispatch
        )
      }
      const response = await putOnboardingFormData({
        accountId: partnerId,
        data: onboardingPayload,
      })
      if (response) {
        const camelizedOnboardingData = JSON.parse(
          JSON.stringify(camelize(get(response, 'data') || {})).replaceAll(
            '\\\\"',
            '"'
          )
        )

        if (
          camelizedOnboardingData.details?.cloudSettings?.cloudCurrencies !==
          undefined
        ) {
          mapCloudCurrenciesToCurrencies(
            camelizedOnboardingData.details.cloudSettings
          )
        }

        const updatedOnboardingJson = {
          ...camelizedOnboardingData.details,
          lockMetadata: {
            ...camelizedOnboardingData.lockMetadata,
          },
        }
        if (!isAutoSave && updatedOnboardingJson.completed?.value) {
          await dispatch(
            updateAppAlert({
              message: isOnboarded
                ? 'Changes saved successfully.'
                : flyOutOnboardingCompletedSuccessfully,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }

        await dispatch(setOnboardingFormData(updatedOnboardingJson))

        if (status === 'lockAcquire' && callBackFlyoutOnboarding) {
          callBackFlyoutOnboarding()
        }

        if (updatedOnboardingJson.completed?.value) {
          setTimeout(async () => {
            await dispatch(getFlyoutListingData({ partnerId, metadata: true }))
          }, 1000)
        }
      }
    } catch (error: any) {
      if (!isAutoSave) {
        const e = error as AxiosError
        const errorResponse = e?.response as AxiosResponse<ErrorResponse>

        dispatch(
          updateAppAlert({
            message: getErrorMessages([RequestFailureMessage])(errorResponse),
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        const globalState = getState()
        errorLogger({ globalState })(
          error as Error,
          errorResponse?.data?.track_id
        )
      } else {
        return error
      }
    } finally {
      !isAutoSave &&
        (await dispatch(stopLoading(LoadingTypes.SUBMIT_FLYOUT_ONBOARDING)))
    }
  }

export const flyOutTestConnection =
  (
    partnerId: string,
    cloudRoleArn: string,
    isMarketplaceCloudSettingsRevampEnabled: boolean,
    updateFlyoutTestConnection: (status: TestConnectionStatusProps) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await updateFlyoutTestConnection({ status: 'loading' })
    const externalId = partnerId
    try {
      const metadataPayload = isMarketplaceCloudSettingsRevampEnabled
        ? { operations: 'test_connection', cloudRoleArn, externalId }
        : { operations: 'test_connection', cloudRoleArn }
      const { data } = await fetchSellerData({
        partnerId,
        metadata: {
          ...snakeize({
            operations: 'test_connection',
            cloudRoleArn,
            externalId,
          }),
          ...snakeize(metadataPayload),
        } as Record<string, unknown>,
      })

      const camelizedData = camelize(data)
      const tested = get(
        camelizedData,
        'operationsResult[0].testConnection.connected',
        false
      )

      const statusValue = tested ? 'success' : 'failure'
      await updateFlyoutTestConnection({ status: statusValue })
    } catch (error) {
      await updateFlyoutTestConnection({ status: 'failure' })
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const flyOutTestConnectionRoleAndDatafeeds =
  (
    partnerId: string,
    cloudRoleArn: string,
    datafeedsS3BucketName: string,
    updateStatus: (status: TestConnectionStatusProps) => void
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await updateStatus({ status: 'loading' })
    const externalId = partnerId
    try {
      const { data } = await fetchSellerData({
        partnerId,
        metadata: {
          ...snakeize({
            operations: 'test_connection',
            cloudRoleArn,
            externalId,
            datafeedsS3BucketName,
          }),
        } as Record<string, unknown>,
      })
      const camelizedData = camelize(data)
      const tested = get(
        camelizedData,
        'operationsResult[0].testConnection.connected',
        false
      )

      const statusValue = tested ? 'success' : 'failure'
      await updateStatus({ status: statusValue })
    } catch (error) {
      await updateStatus({ status: 'failure' })
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    }
  }

export const sendMarketplaceOnboardingData =
  (
    accountId: string,
    onboardingFormData: FlyoutCloudSettingsSectionsProps,
    cloudType: CloudType,
    shouldShowSuccessMessage?: boolean
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.SEND_FLYOUT_ONBOARDING_DATA))

    try {
      const formToApiPayload = convertMicrosoftFormToApiPayload(
        accountId,
        onboardingFormData
      )
      const response = await putFlyoutNewOnboardingFormData({
        accountId,
        data: formToApiPayload,
        cloudType,
      })
      if (response) {
        const microsoftFormData = convertApiResponseToReducer(response.data)
        if (microsoftFormData?.submit) {
          await dispatch(
            updateAppAlert({
              message: flyOutOnboardingCompletedSuccessfully,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }
        await dispatch(updateMicrosoftOnboardingState(microsoftFormData))
        if (shouldShowSuccessMessage) {
          await dispatch(
            updateAppAlert({
              message: NotificationSavedSuccessMessageV2,
              messageType: 'SUCCESS',
              autoClose: true,
            })
          )
        }
      }
      if (response && (response.data as { submit?: boolean }).submit) {
        setTimeout(() => {
          dispatch(getFlyoutListingDataMicrosoft({ accountId }) as any)
        }, 1000)
      }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<ErrorResponse>

      if (errorResponse && errorResponse.status === 409) {
        return
      }

      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.SEND_FLYOUT_ONBOARDING_DATA))
    }
  }

export const checkMicrosoftConnection =
  (
    partnerId: string,
    testConnection: boolean,
    payload: CheckMicrosoftConnectionPayload
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.CHECK_MICROSOFT_CONNECTION))
    try {
      const response = await patchCheckMicrosoftConnection(
        partnerId,
        testConnection,
        payload
      )
      const statusCode = response.status
      if (statusCode === 200) {
        return {
          status: 'success',
        }
      } else {
        return {
          status: 'failure',
        }
      }
    } catch (error) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return {
        status: 'failure',
      }
    } finally {
      await dispatch(stopLoading(LoadingTypes.CHECK_MICROSOFT_CONNECTION))
    }
  }
